import { initAppearance } from './theme.js';
initAppearance();
import { shouldShowLanding, hasLaunchedBefore, rememberLaunch, forgetLaunch } from './bootRouting.js';
import { createControllerChangeHandler } from './swUpdate.js';

// Canvas roundRect polyfill (Safari <15.4, Firefox <99)
if (typeof CanvasRenderingContext2D !== 'undefined' && !CanvasRenderingContext2D.prototype.roundRect) {
  CanvasRenderingContext2D.prototype.roundRect = function(x, y, w, h, radii) {
    const r = typeof radii === 'number' ? radii : (radii?.[0] || 0);
    this.beginPath();
    this.moveTo(x + r, y);
    this.lineTo(x + w - r, y);
    this.quadraticCurveTo(x + w, y, x + w, y + r);
    this.lineTo(x + w, y + h - r);
    this.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
    this.lineTo(x + r, y + h);
    this.quadraticCurveTo(x, y + h, x, y + h - r);
    this.lineTo(x, y + r);
    this.quadraticCurveTo(x, y, x + r, y);
    this.closePath();
  };
}

/** Swap the marketing page out for the editor. No account required. */
async function startEditor() {
  const landing = document.getElementById('landing');
  if (landing) landing.style.display = 'none';
  document.body.classList.add('app-mode');
  document.getElementById('app-root').style.display = 'block';
  await import('./Editor/AEEditor.js');
}

async function boot() {
  // First-time signed-out browser visitors get the marketing page. Everyone
  // else — signed in, inside the Puter shell, or returning after a previous
  // launch — goes straight to the editor. Signing in is only ever needed for
  // the cloud menu items, which ask for it at the point of use.
  const params = new URLSearchParams(window.location.search);
  const showLandingPage = shouldShowLanding({
    hasPuter: !!window.puter,
    env: window.puter?.env,
    signedIn: !!window.puter?.auth?.isSignedIn?.(),
    hasLaunched: hasLaunchedBefore(),
    forceHome: params.has('home'),
    forceEditor: params.has('editor')
  });

  if (showLandingPage) {
    forgetLaunch(); // ?home should stick until the visitor launches again
    const { showLanding } = await import('./Landing/Landing.js');
    showLanding({
      onLaunch: () => {
        rememberLaunch();
        startEditor();
      }
    });
    return;
  }

  if (params.has('editor')) rememberLaunch();
  await startEditor();
}
boot();

// Register service worker for PWA support with update detection
if ('serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/sw.js')
      .then((registration) => {
        console.log('[SW] Service worker registered:', registration.scope);

        // Check for updates periodically (every 60 minutes)
        setInterval(() => {
          registration.update();
        }, 60 * 60 * 1000);

        // Handle updates
        registration.addEventListener('updatefound', () => {
          const newWorker = registration.installing;
          console.log('[SW] New service worker installing...');

          newWorker.addEventListener('statechange', () => {
            if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
              console.log('[SW] New version available');
              // Auto-activate the new service worker
              newWorker.postMessage('skipWaiting');
            }
          });
        });

        // If there's already a waiting worker on page load
        if (registration.waiting && navigator.serviceWorker.controller) {
          registration.waiting.postMessage('skipWaiting');
        }
      })
      .catch((error) => {
        console.warn('[SW] Service worker registration failed:', error);
      });
  });

  // Reload once a new worker takes over (after skipWaiting) — but not on the
  // first install, and not while the editor is recording, loading or exporting.
  // See swUpdate.js. `controller` is read now, at page load, before register()
  // can change it.
  navigator.serviceWorker.addEventListener('controllerchange', createControllerChangeHandler({
    hadController: !!navigator.serviceWorker.controller,
    isBusy: () => !!document.querySelector('ae-editor')?.isBusy?.(),
    reload: () => window.location.reload()
  }));
}
